import { useCallback, useContext } from 'react';

import {
  ProductCatalogV2,
  ProductSlug,
  useDealProductsV2Query,
  useUpdateDealProductsV2Mutation,
} from '../gql/generated/graphql';

import { DealContext } from '../libs/DealContext';
import { PersistedPackageConfig } from '../types/products';

type DealProductInput = {
  productCatalogId: number;
  price: number;
  cost: number;
  term?: string;
  coverageType?: string;
  penRateId?: number;
};

export type PenRateData = {
  vsc?: {
    rateId?: number;
  };
  gap?: {
    rateId?: number;
  };
};

export type FinancialInfoPrices = {
  vscPrice?: number | null;
  vscCost?: number | null;
  vscTerm?: string | null;
  vscType?: string | null;
  gapPrice?: number | null;
  gapCost?: number | null;
  tireWheelPrice?: number | null;
  tireWheelCost?: number | null;
  keyReplacementPrice?: number | null;
  keyReplacementCost?: number | null;
};

type SaveProductSelectionOptions = {
  selectedPackageSlotIndex?: number | null;
  packageConfigs?: PersistedPackageConfig[];
};

/**
 * Hook for managing deal products - fetching and saving product selections.
 */
export const useDealProducts = () => {
  const { deal } = useContext(DealContext);
  const dealId = deal?.id ? Number(deal.id) : 0;

  // Query for current deal products
  const {
    data,
    loading: queryLoading,
    error: queryError,
    refetch,
  } = useDealProductsV2Query({
    variables: { dealId },
    skip: !dealId,
  });

  // Mutation for updating deal products
  const [updateMutation, { loading: mutationLoading, error: mutationError }] =
    useUpdateDealProductsV2Mutation();

  const dealProducts = data?.dealProductsV2 ?? [];

  /**
   * Save selected products to the deal.
   * Selection is derived server-side from the submitted product combination.
   */
  const saveProducts = useCallback(
    async (products: DealProductInput[], options: SaveProductSelectionOptions = {}) => {
      if (!dealId) {
        throw new Error('No deal ID available');
      }

      const result = await updateMutation({
        variables: {
          input: {
            dealId,
            products,
            selectedPackageSlotIndex: options.selectedPackageSlotIndex,
            packageConfigs: options.packageConfigs?.map((config) => ({
              slotIndex: config.slotIndex,
              selectedProductSlugs: config.selectedProductSlugs,
            })),
          },
        },
      });

      return result;
    },
    [dealId, updateMutation],
  );

  /**
   * Save products based on selected product catalog items.
   * Uses financialInfoPrices (from the UI form) if provided, otherwise falls back to catalog base_price/base_cost.
   * Optionally accepts penRateData to associate PEN rate IDs with VSC/GAP products.
   * Note: Tax amounts are assigned by the backend from financial_info.warranty_tax_amount.
   */
  const saveProductsFromCatalog = useCallback(
    async (
      selectedProducts: ProductCatalogV2[],
      penRateData?: PenRateData,
      financialInfoPrices?: FinancialInfoPrices,
      options?: SaveProductSelectionOptions,
    ) => {
      const getExistingDealProduct = (slug: ProductSlug) =>
        deal.deal_products?.find((dealProduct) => dealProduct.product_catalog?.slug === slug) ??
        dealProducts.find((dealProduct) => dealProduct.product_catalog?.slug === slug);

      const products: DealProductInput[] = selectedProducts.map((product) => {
        const isVsc = product.slug === ProductSlug.Vsc;
        const isGap = product.slug === ProductSlug.Gap;
        const isTireWheel = product.slug === ProductSlug.TireWheel;
        const isKeyReplacement = product.slug === ProductSlug.KeyReplacement;
        const rateData = isVsc ? penRateData?.vsc : isGap ? penRateData?.gap : undefined;
        const existingDealProduct = getExistingDealProduct(product.slug);

        // Preserve the current deal_products row when available and only fall back
        // to catalog defaults if the deal has no value for that slug yet.
        let price = existingDealProduct?.price ?? product.base_price;
        let cost = existingDealProduct?.cost ?? product.base_cost;
        let term = existingDealProduct?.term ?? undefined;
        // coverage_type (the VSC tier) is the rep's selection — persist it to
        // deal_products so it survives without the legacy financial_info bridge.
        let coverageType = existingDealProduct?.coverage_type ?? undefined;

        if (isVsc && financialInfoPrices) {
          price = financialInfoPrices.vscPrice ?? price;
          cost = financialInfoPrices.vscCost ?? cost;
          term = financialInfoPrices.vscTerm ?? term;
          coverageType = financialInfoPrices.vscType ?? coverageType;
        } else if (isGap && financialInfoPrices) {
          price = financialInfoPrices.gapPrice ?? price;
          cost = financialInfoPrices.gapCost ?? cost;
        } else if (isTireWheel && financialInfoPrices) {
          price = financialInfoPrices.tireWheelPrice ?? price;
          cost = financialInfoPrices.tireWheelCost ?? cost;
        } else if (isKeyReplacement && financialInfoPrices) {
          price = financialInfoPrices.keyReplacementPrice ?? price;
          cost = financialInfoPrices.keyReplacementCost ?? cost;
        }

        return {
          productCatalogId: product.id,
          price,
          cost,
          term,
          coverageType,
          penRateId: rateData?.rateId,
        };
      });

      return saveProducts(products, options);
    },
    [deal.deal_products, dealProducts, saveProducts],
  );

  /**
   * Clear all products from the deal (select "No Protection").
   */
  const clearProducts = useCallback(
    async (options?: SaveProductSelectionOptions) => saveProducts([], options),
    [saveProducts],
  );

  return {
    dealProducts,
    loading: queryLoading || mutationLoading,
    error: queryError || mutationError,
    queryError,
    mutationError,
    saveProducts,
    saveProductsFromCatalog,
    clearProducts,
    refetch,
  };
};
